import { useState, useEffect } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';

const SignupForm = ({ switchToLogin, setAuthError, setAuthMessage }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    company: '',
    company_website: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    try {
      const savedGuestScan = localStorage.getItem('seenbyai_last_guest_scan');
      if (savedGuestScan) {
        const parsed = JSON.parse(savedGuestScan);
        setFormData(prev => ({
          ...prev,
          company: prev.company || parsed.company || '',
          company_website: prev.company_website || parsed.website || '',
        }));
      }
    } catch (e) {
      console.warn("Error reading guest scan from localStorage:", e);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');
    setAuthMessage('');

    try {
      let guestScanPayload = null;
      try {
        const savedGuestScan = localStorage.getItem('seenbyai_last_guest_scan');
        if (savedGuestScan) {
          guestScanPayload = JSON.parse(savedGuestScan);
        }
      } catch (e) {
        // ignore storage parse error
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          company_name: formData.company,
          company_website: formData.company_website,
          guest_scan: guestScanPayload,
        }),
      });

      let result = {};
      try {
        result = await response.json();
      } catch (jsonErr) {
        // response might not be JSON
      }

      if (!response.ok) {
        let errorMsg = result?.detail || result?.message || result?.error;
        if (Array.isArray(errorMsg)) {
          errorMsg = errorMsg.map(err => (typeof err === 'object' && err?.msg) ? err.msg : (typeof err === 'string' ? err : JSON.stringify(err))).join(', ');
        } else if (typeof errorMsg === 'object' && errorMsg !== null) {
          errorMsg = errorMsg.message || JSON.stringify(errorMsg);
        }

        if (!errorMsg || typeof errorMsg !== 'string') {
          if (response.status === 400) {
            errorMsg = 'Invalid signup details. Please check your information.';
          } else if (response.status === 500) {
            errorMsg = 'Server error during signup. Please try again later.';
          } else {
            errorMsg = 'Signup failed. Please try again.';
          }
        }

        setAuthError(errorMsg);
        return;
      }

      // Keep guest_scan in localStorage so UserDashboard can also claim it if email confirmation or signin is required
      setFormData({ email: '', password: '', company: '', company_website: '', });
      setAuthMessage(result.message || 'Account created! Check your email to confirm.');
      switchToLogin();

    } catch (err) {
      console.error('Signup error:', err);
      setAuthError('Network error during signup. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <form className="auth-form" onSubmit={handleSignup}>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          className="form-input"
          placeholder='Email Address'
          required
          disabled={loading}
          autoComplete="email"
        />
      </div>

      <div className="form-group">
        <label htmlFor="password">Password</label>
        <div className="password-input-container">
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            value={formData.password}
            onChange={handleChange}
            className="form-input"
            placeholder='Password'
            required
            disabled={loading}
            autoComplete="new-password"
          />
          <button
            type="button"
            className="password-toggle"
            onClick={togglePasswordVisibility}
            disabled={loading}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
          </button>
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="company">Company Name</label>
        <input
          id="company"
          name="company"
          type="text"
          value={formData.company}
          onChange={handleChange}
          className="form-input"
          placeholder='Company Name'
          required
          disabled={loading}
          autoComplete="organization"
        />
      </div>
      <div className="form-group">
        <label htmlFor="company_website">Company Website</label>
        <input
          id="company_website"
          name="company_website"
          type="text"
          value={formData.company_website}
          onChange={handleChange}
          className="form-input"
          placeholder='e.g https://yourcompanysite.com'
          required
          disabled={loading}
          autoComplete="url"
        />
      </div>

      <button
        type="submit"
        className="submit-button"
        disabled={loading}
      >
        {loading ? (
          <>
            <span className="loading-spinner">↻</span> Creating Account...
          </>
        ) : 'Sign Up'}
      </button>
    </form>
  );
};

export default SignupForm;